import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { PiggyBank, TrendingUp, DollarSign, Target, Info, Calendar, Calculator, Check } from "lucide-react";

/* ─── DobPicker — 3 independent dropdowns, never auto-advances ────────── */
const MONTHS_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 121 }, (_, i) => CURRENT_YEAR - i);

const daysInMonth = (year, monthIndex) => {
  if (monthIndex == null || year == null) return 31;
  return new Date(year, monthIndex + 1, 0).getDate();
};

function DobPicker({ onSave }) {
  const [month, setMonth] = React.useState("");
  const [day, setDay] = React.useState("");
  const [year, setYear] = React.useState("");

  const ready = month !== "" && day !== "" && year !== "";
  const dayMax = daysInMonth(year ? parseInt(year) : null, month !== "" ? parseInt(month) : null);
  const dayOptions = Array.from({ length: dayMax }, (_, i) => i + 1);

  const handleSave = () => {
    if (!ready) return;
    // Build ISO date string YYYY-MM-DD
    const m = String(parseInt(month) + 1).padStart(2, "0");
    const d = String(parseInt(day)).padStart(2, "0");
    const iso = `${year}-${m}-${d}`;
    onSave(iso);
  };

  return (
    <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4 mb-6">
      <div className="flex items-start gap-3 mb-4">
        <Calendar size={18} className="text-arc-gold flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-arc-ink font-medium text-sm mb-1">
            Set your date of birth once
          </p>
          <p className="text-arc-muted text-xs">
            We'll auto-calculate your current age. You won't need to re-enter it.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col">
          <span className="eyebrow mb-2">Month</span>
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="px-3 py-2 rounded-lg text-sm min-w-[140px]"
          >
            <option value="">Select month</option>
            {MONTHS_FULL.map((m, i) => (
              <option key={i} value={i}>{m}</option>
            ))}
          </select>
        </label>

        <label className="flex flex-col">
          <span className="eyebrow mb-2">Day</span>
          <select
            value={day}
            onChange={(e) => setDay(e.target.value)}
            className="px-3 py-2 rounded-lg text-sm min-w-[80px] num"
          >
            <option value="">Day</option>
            {dayOptions.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </label>

        <label className="flex flex-col">
          <span className="eyebrow mb-2">Year</span>
          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="px-3 py-2 rounded-lg text-sm min-w-[100px] num"
          >
            <option value="">Year</option>
            {YEAR_OPTIONS.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>

        <button
          onClick={handleSave}
          disabled={!ready}
          className="btn-tactile btn-gold px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Check size={14} strokeWidth={2.5} />
          Save
        </button>
      </div>
    </div>
  );
}

/**
 * Retirement Projection
 *
 * Accumulation phase + drawdown phase, with:
 *   - DOB-derived current age (saved once, no re-entry)
 *   - Growth rate input that survives backspace (independent string state)
 *   - Use-actual-DCA toggle (pulls from DCA tab)
 *   - 4% / 5% / fixed-years drawdown calculator
 *   - Current portfolio value = manual investments + on-chain wallets
 */
const RetirementTab = ({
  userAge,
  setUserAge,
  dob,
  setDob,
  netWorth,
  investments,
  wallets = [],
  netIncomeData,
  dcaData,
  investmentHistory,
  retirementSettings,
  setRetirementSettings,
}) => {
  /* ─── Parse a YYYY-MM-DD string as a LOCAL date (no timezone shift) ─ */
  const parseLocalDate = (iso) => {
    if (!iso) return null;
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const [, y, mo, d] = m.map(Number);
    return new Date(y, mo - 1, d);
  };

  /* ─── Derive age from DOB if available ────────────────────────────── */
  const ageFromDob = (dobStr) => {
    const d = parseLocalDate(dobStr);
    if (!d || isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return age;
  };

  /* ─── Format DOB for display (avoiding the same timezone shift) ───── */
  const formatDob = (dobStr) => {
    const d = parseLocalDate(dobStr);
    if (!d || isNaN(d.getTime())) return dobStr || "";
    return d.toLocaleDateString();
  };

  // If DOB is set, age is derived. Otherwise we use the userAge state.
  const derivedAge = ageFromDob(dob);
  const effectiveAge = derivedAge != null ? derivedAge : userAge;

  /* ─── Local state — initialized from saved retirementSettings ─────── */
  const init = retirementSettings || {};
  const [retireAge, setRetireAge] = useState(init.retireAge ?? 65);
  const [growthRate, setGrowthRate] = useState(init.growthRate ?? 0.07);
  const [growthRateText, setGrowthRateText] = useState(
    ((init.growthRate ?? 0.07) * 100).toFixed(2)
  );
  const [projection, setProjection] = useState([]);
  const [autoEstimate, setAutoEstimate] = useState(init.autoEstimate ?? false);
  const [timeRange, setTimeRange] = useState(init.timeRange ?? "all");
  const [contributionMode, setContributionMode] = useState(
    init.contributionMode ?? "simulated"
  );
  const [simulatedMonthly, setSimulatedMonthly] = useState(
    init.simulatedMonthly ?? ""
  );
  const [drawdownYears, setDrawdownYears] = useState(init.drawdownYears ?? 30);
  const [drawdownYearsText, setDrawdownYearsText] = useState(
    String(init.drawdownYears ?? 30)
  );
  const [drawdownReturn, setDrawdownReturn] = useState(init.drawdownReturn ?? 0.06);
  const [drawdownReturnText, setDrawdownReturnText] = useState(
    ((init.drawdownReturn ?? 0.06) * 100).toFixed(1)
  );

  /* ─── Hydrate when retirementSettings loads from Firestore ────────── */
  // If settings come in async (after mount), sync local state once.
  const hydratedRef = React.useRef(false);
  useEffect(() => {
    if (!retirementSettings || hydratedRef.current) return;
    if (Object.keys(retirementSettings).length === 0) return;
    hydratedRef.current = true;
    if (retirementSettings.retireAge != null) setRetireAge(retirementSettings.retireAge);
    if (retirementSettings.growthRate != null) {
      setGrowthRate(retirementSettings.growthRate);
      setGrowthRateText((retirementSettings.growthRate * 100).toFixed(2));
    }
    if (retirementSettings.autoEstimate != null) setAutoEstimate(retirementSettings.autoEstimate);
    if (retirementSettings.timeRange != null) setTimeRange(retirementSettings.timeRange);
    if (retirementSettings.contributionMode != null) setContributionMode(retirementSettings.contributionMode);
    if (retirementSettings.simulatedMonthly != null) setSimulatedMonthly(retirementSettings.simulatedMonthly);
    if (retirementSettings.drawdownYears != null) {
      setDrawdownYears(retirementSettings.drawdownYears);
      setDrawdownYearsText(String(retirementSettings.drawdownYears));
    }
    if (retirementSettings.drawdownReturn != null) {
      setDrawdownReturn(retirementSettings.drawdownReturn);
      setDrawdownReturnText((retirementSettings.drawdownReturn * 100).toFixed(1));
    }
  }, [retirementSettings]);

  /* ─── Persist any change to parent → autosaves to Firestore ───────── */
  useEffect(() => {
    if (!setRetirementSettings) return;
    setRetirementSettings({
      retireAge,
      growthRate,
      autoEstimate,
      timeRange,
      contributionMode,
      simulatedMonthly,
      drawdownYears,
      drawdownReturn,
    });
  }, [
    retireAge,
    growthRate,
    autoEstimate,
    timeRange,
    contributionMode,
    simulatedMonthly,
    drawdownYears,
    drawdownReturn,
    setRetirementSettings,
  ]);

  /* ─── Flatten investment history for realized growth calc ─────────── */
  const flattenInvestmentHistory = (history) => {
    if (!history || !Object.keys(history).length) return [];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const data = [];
    Object.keys(history)
      .sort((a, b) => Number(a) - Number(b))
      .forEach((year) => {
        months.forEach((m) => {
          if (history[year][m] !== undefined) {
            data.push({ yearMonth: `${m} ${year}`, value: history[year][m] });
          }
        });
      });
    const cleaned = data.filter((d) => typeof d.value === "number" && d.value > 0);
    if (timeRange !== "all") {
      const monthsToKeep = parseInt(timeRange);
      return cleaned.slice(-monthsToKeep);
    }
    return cleaned;
  };
  const historicalData = flattenInvestmentHistory(investmentHistory);

  /* ─── Realized growth rate (clamped) ──────────────────────────────── */
  const calculateRealizedGrowthRate = (data) => {
    if (data.length < 2) return 0.07;
    const startValue = data[0].value;
    const endValue = data[data.length - 1].value;
    const months = data.length - 1 || 1;
    const cagr = Math.pow(endValue / startValue, 12 / months) - 1;
    if (!isFinite(cagr)) return 0.07;
    return Math.max(-0.2, Math.min(0.15, cagr));
  };
  const realizedGrowthRate = calculateRealizedGrowthRate(historicalData);

  /* ─── Current Portfolio Value = manual investments + on-chain wallets ── */
  const totalInvestments = (investments || []).reduce(
    (s, i) => s + (i.amount || 0),
    0
  );
  const totalCrypto = (wallets || []).reduce(
    (s, w) =>
      s +
      (typeof w.manualValue === "number" && !isNaN(w.manualValue)
        ? w.manualValue
        : w.lastValue || 0),
    0
  );
  const currentPortfolioValue = totalInvestments + totalCrypto;

  /* ─── Average DCA from DCA tab ────────────────────────────────────── */
  const actualDcaMonthly =
    dcaData?.monthlyAmount > 0
      ? dcaData.monthlyAmount
      : dcaData?.history?.length
      ? dcaData.history.reduce((s, r) => s + (r.dcaContribution || 0), 0) /
        dcaData.history.length
      : 0;

  const monthlyContribution =
    contributionMode === "simulated" && simulatedMonthly && parseFloat(simulatedMonthly) > 0
      ? parseFloat(simulatedMonthly)
      : actualDcaMonthly;

  const yearsToRetire = Math.max(0, retireAge - effectiveAge);

  /* ─── Auto-estimate sync ──────────────────────────────────────────── */
  useEffect(() => {
    if (autoEstimate) {
      setGrowthRate(realizedGrowthRate);
      setGrowthRateText((realizedGrowthRate * 100).toFixed(2));
    }
  }, [autoEstimate, realizedGrowthRate]);

  /* ─── Accumulation projection ─────────────────────────────────────── */
  useEffect(() => {
    let future = currentPortfolioValue;
    const data = [];
    for (let y = 1; y <= yearsToRetire; y++) {
      future = (future + monthlyContribution * 12) * (1 + growthRate);
      data.push({
        yearMonth: `Year ${new Date().getFullYear() + y}`,
        value: Math.round(future),
      });
    }
    setProjection(data);
  }, [retireAge, effectiveAge, growthRate, currentPortfolioValue, monthlyContribution, yearsToRetire]);

  const projectedValue =
    projection.length > 0 ? projection[projection.length - 1].value : currentPortfolioValue;

  /* ─── Drawdown phase math ─────────────────────────────────────────── */
  // 4% Rule: ~perpetual safe annual withdrawal
  const safeAnnual4 = projectedValue * 0.04;
  const safeAnnual5 = projectedValue * 0.05;

  // Fixed-period amortization: PMT = P * r / (1 - (1+r)^-n)
  // Where P=principal, r=monthly return rate, n=months
  const monthlyDrawdownReturn = drawdownReturn / 12;
  const drawdownMonths = drawdownYears * 12;
  const fixedMonthlyDraw =
    drawdownMonths > 0 && monthlyDrawdownReturn > 0
      ? (projectedValue * monthlyDrawdownReturn) /
        (1 - Math.pow(1 + monthlyDrawdownReturn, -drawdownMonths))
      : drawdownMonths > 0
      ? projectedValue / drawdownMonths
      : 0;
  const fixedAnnualDraw = fixedMonthlyDraw * 12;

  /* ─── Combined chart data ─────────────────────────────────────────── */
  const combinedData = [
    ...historicalData.map((d) => ({ ...d, type: "Historical" })),
    ...projection.map((d) => ({ ...d, type: "Projected" })),
  ];

  /* ─── Number formatting ───────────────────────────────────────────── */
  const fmtCurrency = (n) =>
    `$${(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  const fmtAxis = (v) => {
    const a = Math.abs(v);
    if (a >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (a >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
    return `$${v}`;
  };

  /* ─── Handlers ────────────────────────────────────────────────────── */
  const handleGrowthRateText = (e) => {
    const text = e.target.value;
    setGrowthRateText(text);
    // Allow empty / partial typing without forcing the value
    if (text === "" || text === "-" || text === ".") return;
    const parsed = parseFloat(text);
    if (!isNaN(parsed)) setGrowthRate(parsed / 100);
  };
  const handleGrowthRateBlur = () => {
    // Format to 2 decimals on blur if it's a number
    const parsed = parseFloat(growthRateText);
    if (!isNaN(parsed)) setGrowthRateText(parsed.toFixed(2));
  };

  const handleDob = (e) => setDob && setDob(e.target.value);

  /* ─── Render ──────────────────────────────────────────────────────── */
  return (
    <div className="surface p-6 sm:p-8">
      {/* Header */}
      <div className="mb-6">
        <p className="eyebrow mb-2">Projection</p>
        <h2 className="headline text-2xl mb-2">Retirement</h2>
        <p className="text-arc-muted text-sm leading-relaxed max-w-2xl">
          Long-term projections based on your portfolio + DCA plan, plus a drawdown
          calculator showing what you can sustainably withdraw once you hit your number.
        </p>
      </div>

      {/* Date of Birth (saves once) */}
      {!dob && <DobPicker onSave={(iso) => handleDob({ target: { value: iso } })} />}
      {dob && (
        <div className="rounded-xl border border-arc-line bg-white/[0.02] px-4 py-2 mb-6 flex items-center gap-3 text-xs">
          <Calendar size={14} className="text-arc-gold" />
          <span className="text-arc-muted">Date of birth on file:</span>
          <span className="num text-arc-ink">{formatDob(dob)}</span>
          <span className="text-arc-dim">·</span>
          <span className="num text-arc-light">{effectiveAge} years old</span>
          <button
            onClick={() => setDob && setDob("")}
            className="ml-auto text-arc-dim hover:text-arc-ink text-[10px] uppercase tracking-[0.14em] font-mono"
          >
            Edit
          </button>
        </div>
      )}

      {/* How-to box */}
      <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4 mb-6 flex gap-3">
        <Info size={18} className="text-arc-gold flex-shrink-0 mt-0.5" />
        <div className="text-sm text-arc-muted leading-relaxed">
          <p className="text-arc-ink font-medium mb-1.5">How to use this tool</p>
          <ul className="space-y-1">
            <li>
              <span className="text-arc-light">Growth Period</span> — sample your
              actual portfolio history (3 / 6 months / all-time) for the realized growth rate.
            </li>
            <li>
              <span className="text-arc-light">Auto-estimate</span> — fills growth rate from real data, clamped to 0–15%. Always overridable.
            </li>
            <li>
              <span className="text-arc-light">Contribution mode</span> — use your actual DCA from the DCA tab, or simulate "what-if" amounts.
            </li>
            <li>
              <span className="text-arc-light">Drawdown phase</span> — see what you can sustainably withdraw once you hit your number.
            </li>
          </ul>
        </div>
      </div>

      {/* Inputs row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {/* Current age */}
        <label className="flex flex-col">
          <span className="eyebrow mb-2">
            Current Age{derivedAge != null ? " (from DOB)" : ""}
          </span>
          <input
            type="number"
            min={0}
            max={120}
            value={effectiveAge}
            onChange={(e) =>
              !derivedAge && setUserAge(parseInt(e.target.value) || 0)
            }
            disabled={derivedAge != null}
            className="px-3 py-2 rounded-lg text-sm num"
          />
        </label>

        {/* Retirement age */}
        <label className="flex flex-col">
          <span className="eyebrow mb-2">Retirement Age</span>
          <input
            type="number"
            min={0}
            max={120}
            value={retireAge}
            onChange={(e) => setRetireAge(parseInt(e.target.value) || 65)}
            className="px-3 py-2 rounded-lg text-sm num"
          />
        </label>

        {/* Annual growth rate (with backspace fix) */}
        <div className="flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <span className="eyebrow">Annual Growth Rate</span>
            <label className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.14em] text-arc-muted cursor-pointer select-none">
              <input
                type="checkbox"
                checked={autoEstimate}
                onChange={() => setAutoEstimate(!autoEstimate)}
                className="accent-arc-gold"
              />
              Auto
            </label>
          </div>
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={growthRateText}
              onChange={handleGrowthRateText}
              onBlur={handleGrowthRateBlur}
              className="w-full px-3 py-2 pr-7 rounded-lg text-sm num"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-arc-dim text-sm">
              %
            </span>
          </div>
        </div>

        {/* Growth period selector */}
        <label className="flex flex-col">
          <span className="eyebrow mb-2">Growth Period</span>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 rounded-lg text-sm"
          >
            <option value="all">All-time</option>
            <option value="3">Last 3 months</option>
            <option value="6">Last 6 months</option>
          </select>
        </label>
      </div>

      {/* Contribution mode toggle */}
      <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4 mb-6">
        <p className="eyebrow mb-3">Monthly Contribution</p>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setContributionMode("actual")}
            className={`btn-tactile flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
              contributionMode === "actual"
                ? "bg-gradient-to-b from-arc-light to-arc-gold text-arc-bg border-arc-deep shadow-glow"
                : "bg-white/[0.03] text-arc-muted border-arc-line hover:text-arc-ink"
            }`}
          >
            Use My Actual DCA
          </button>
          <button
            onClick={() => setContributionMode("simulated")}
            className={`btn-tactile flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
              contributionMode === "simulated"
                ? "bg-gradient-to-b from-arc-light to-arc-gold text-arc-bg border-arc-deep shadow-glow"
                : "bg-white/[0.03] text-arc-muted border-arc-line hover:text-arc-ink"
            }`}
          >
            Simulate "What If"
          </button>
          <div className="flex-1 min-w-[200px]">
            {contributionMode === "actual" ? (
              <div className="px-3 py-2 rounded-lg bg-white/[0.02] border border-arc-line text-sm">
                <span className="text-arc-muted">Pulled from DCA tab: </span>
                <span className="num text-arc-gold">{fmtCurrency(actualDcaMonthly)}</span>
                <span className="text-arc-muted">/mo</span>
              </div>
            ) : (
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-arc-dim text-sm">
                  $
                </span>
                <input
                  type="number"
                  min={0}
                  value={simulatedMonthly}
                  placeholder="Enter monthly amount"
                  onChange={(e) => setSimulatedMonthly(e.target.value)}
                  className="w-full pl-7 pr-3 py-2 rounded-lg text-sm num"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4 mb-6">
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={combinedData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="yearMonth" hide={combinedData.length > 40} stroke="#a1a1aa" />
            <YAxis stroke="#a1a1aa" tickFormatter={fmtAxis} />
            <Tooltip
              contentStyle={{
                background: "#17171a",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: "0.75rem",
                color: "#fafafa",
              }}
              formatter={(v) => fmtCurrency(v)}
            />
            <Legend wrapperStyle={{ color: "#a1a1aa" }} />
            <Bar
              dataKey="value"
              name="Historical"
              data={combinedData.filter((d) => d.type === "Historical")}
              fill="#a8916a"
              isAnimationActive={false}
            />
            <Bar
              dataKey="value"
              name="Projected"
              data={combinedData.filter((d) => d.type === "Projected")}
              fill="#c4b287"
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Stats — accumulation phase */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4 flex items-center gap-3">
          <div className="p-2 rounded-xl border border-arc-line bg-white/[0.03]">
            <PiggyBank className="text-arc-gold" size={20} />
          </div>
          <div className="min-w-0">
            <p className="eyebrow mb-0.5">Current Portfolio Value</p>
            <p className="text-lg num text-arc-ink truncate">{fmtCurrency(currentPortfolioValue)}</p>
            <p className="text-[10px] text-arc-dim mt-0.5 num">
              {fmtCurrency(totalInvestments)} manual + {fmtCurrency(totalCrypto)} on-chain
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4 flex items-center gap-3">
          <div className="p-2 rounded-xl border border-arc-line bg-white/[0.03]">
            <DollarSign className="text-arc-gold" size={20} />
          </div>
          <div className="min-w-0">
            <p className="eyebrow mb-0.5">
              {contributionMode === "actual" ? "Actual DCA / mo" : "Simulated DCA / mo"}
            </p>
            <p className="text-lg num text-arc-ink truncate">{fmtCurrency(monthlyContribution)}</p>
          </div>
        </div>

        <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4 flex items-center gap-3">
          <div className="p-2 rounded-xl border border-arc-line bg-white/[0.03]">
            <TrendingUp className="text-arc-gold" size={20} />
          </div>
          <div className="min-w-0">
            <p className="eyebrow mb-0.5">{autoEstimate ? "Realized Growth" : "Manual Growth"}</p>
            <p className="text-lg num text-arc-ink truncate">{(growthRate * 100).toFixed(2)}%</p>
          </div>
        </div>

        <div className="rounded-2xl border border-arc-deep bg-gradient-to-br from-arc-light/10 to-arc-deep/5 p-4 flex items-center gap-3">
          <div className="p-2 rounded-xl border border-arc-deep bg-gradient-to-br from-arc-light/20 to-arc-deep/10">
            <Target className="text-arc-light" size={20} />
          </div>
          <div className="min-w-0">
            <p className="eyebrow mb-0.5">Projected at {retireAge}</p>
            <p className="text-lg num text-arc-gold truncate">{fmtCurrency(projectedValue)}</p>
            <p className="text-[10px] text-arc-dim mt-0.5">in {yearsToRetire} years</p>
          </div>
        </div>
      </div>

      {/* DRAWDOWN PHASE — what you can sustainably withdraw */}
      <div className="rounded-2xl border border-arc-deep bg-gradient-to-br from-arc-light/5 to-arc-deep/5 p-5 sm:p-6 mb-2">
        <div className="flex items-center gap-2 mb-3">
          <Calculator size={16} className="text-arc-gold" />
          <p className="eyebrow">Drawdown Phase</p>
        </div>
        <h3 className="headline text-xl mb-2">
          What you can withdraw once you hit your number
        </h3>
        <p className="text-arc-muted text-sm leading-relaxed mb-5 max-w-2xl">
          Based on your projected portfolio of{" "}
          <span className="num text-arc-gold">{fmtCurrency(projectedValue)}</span> at age{" "}
          <span className="num text-arc-ink">{retireAge}</span>. Pick a preset below or set
          your own assumptions in the inputs further down.
        </p>

        {/* Preset chips — one click sets drawdown years + return to a preset
            scenario. Active preset (matches current inputs) gets a gold border.
            This replaces the old "three equal cards" UI which implied 4%/5%
            rules were competing options instead of historical benchmarks. */}
        {(() => {
          const presets = [
            {
              key: "conservative",
              label: "Conservative",
              icon: "🛡",
              years: 30,
              ret: 0.05,
              hint: "30 yrs · 5% return · low risk",
            },
            {
              key: "moderate",
              label: "Moderate",
              icon: "⚖",
              years: 25,
              ret: 0.07,
              hint: "25 yrs · 7% return · balanced",
            },
            {
              key: "aggressive",
              label: "Aggressive",
              icon: "📈",
              years: 20,
              ret: 0.10,
              hint: "20 yrs · 10% return · risk on",
            },
          ];
          const matchesPreset = (p) =>
            drawdownYears === p.years &&
            Math.abs(drawdownReturn - p.ret) < 0.0005;
          const applyPreset = (p) => {
            setDrawdownYears(p.years);
            setDrawdownYearsText(String(p.years));
            setDrawdownReturn(p.ret);
            setDrawdownReturnText((p.ret * 100).toFixed(1));
          };
          return (
            <div className="mb-5">
              <p className="eyebrow mb-2">Pick a scenario</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {presets.map((p) => {
                  const active = matchesPreset(p);
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => applyPreset(p)}
                      className={`btn-tactile text-left rounded-xl border p-3 transition-all ${
                        active
                          ? "border-arc-gold bg-arc-gold/10"
                          : "border-arc-line bg-white/[0.02] hover:border-arc-gold/40 hover:bg-white/[0.05]"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-base">{p.icon}</span>
                        <span
                          className={`font-semibold text-sm ${
                            active ? "text-arc-gold" : "text-arc-ink"
                          }`}
                        >
                          {p.label}
                        </span>
                        {active && (
                          <span className="ml-auto text-[10px] font-mono uppercase tracking-[0.14em] text-arc-gold">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-arc-dim font-mono">{p.hint}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Primary withdrawal card — the ONE answer based on current inputs.
            Adapts styling for high-return warning state. */}
        {(() => {
          const isHighReturn = drawdownReturn > 0.10;
          const annualInterest = projectedValue * drawdownReturn;
          const isPerpetuity =
            annualInterest > 0 &&
            Math.abs(fixedAnnualDraw - annualInterest) / annualInterest < 0.05;
          return (
            <div
              className={`rounded-2xl border p-5 sm:p-6 mb-4 ${
                isHighReturn
                  ? "border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-arc-deep/5"
                  : "border-arc-deep bg-gradient-to-br from-arc-light/10 to-arc-deep/5"
              }`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                <div>
                  <p className="eyebrow mb-1">Your annual withdrawal</p>
                  <p className="text-xs text-arc-muted">
                    {drawdownYears} years @ {(drawdownReturn * 100).toFixed(1)}% return
                  </p>
                </div>
                {isHighReturn && (
                  <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-amber-400 px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/30">
                    ⚠ Aggressive assumption
                  </span>
                )}
              </div>
              <p className="num text-4xl sm:text-5xl text-arc-gold mb-2 leading-tight">
                {fmtCurrency(fixedAnnualDraw)}
              </p>
              <p className="text-sm text-arc-muted">
                per year · ≈{" "}
                <span className="num text-arc-ink">{fmtCurrency(fixedMonthlyDraw)}</span>{" "}
                per month
              </p>
              {!isHighReturn && isPerpetuity && (
                <p className="mt-3 text-[11px] font-mono uppercase tracking-[0.14em] text-arc-light">
                  ≈ pure interest at this rate · principal preserved
                </p>
              )}
            </div>
          );
        })()}

        {/* Benchmark strip — Bengen's 4% rule and 5% rule shown as historical
            references, NOT as competing options. Just sanity checks. */}
        <div className="mb-5 rounded-xl border border-arc-line bg-white/[0.02] p-3">
          <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-arc-dim mb-2">
            For reference · historical benchmarks
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-arc-muted">
                <span className="font-semibold text-arc-ink">Bengen 4% rule</span> · 30-yr safe
              </span>
              <span className="num text-arc-gold whitespace-nowrap">
                {fmtCurrency(safeAnnual4)}/yr
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-arc-muted">
                <span className="font-semibold text-arc-ink">5% rule</span> · ~25-yr aggressive
              </span>
              <span className="num text-arc-gold whitespace-nowrap">
                {fmtCurrency(safeAnnual5)}/yr
              </span>
            </div>
          </div>
          <p className="text-[10px] text-arc-dim mt-2 leading-relaxed">
            Bengen tested every 30-yr window in US market history. Withdrawing 4% of
            starting portfolio (inflation-adjusted) survived every scenario including
            the Great Depression. These are anchors — compare your number above against
            them as a reality check.
          </p>
        </div>

        {/* Perpetuity callout — only show when high-return input is causing
            the Fixed Period to be near pure interest. Helps the user see
            why their 40-yr number doesn't change much when they tweak years. */}
        {(() => {
          const annualInterest = projectedValue * drawdownReturn;
          const isPerpetuity =
            drawdownReturn > 0.10 &&
            annualInterest > 0 &&
            Math.abs(fixedAnnualDraw - annualInterest) / annualInterest < 0.05;
          if (!isPerpetuity) return null;
          return (
            <div className="mb-5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2">
              <span className="text-amber-400 text-sm flex-shrink-0">ℹ</span>
              <p className="text-xs text-amber-200 leading-relaxed">
                <span className="font-semibold">Why doesn't the Fixed Period change much when you tweak years?</span>{" "}
                At {(drawdownReturn * 100).toFixed(0)}% return, your portfolio of{" "}
                <span className="num">{fmtCurrency(projectedValue)}</span> generates{" "}
                <span className="num">{fmtCurrency(annualInterest)}</span> per year in pure interest.
                Withdrawing at this rate is sustainable indefinitely — the years input barely matters because
                the principal isn't depleting. Try dropping the expected return to 5–7% to see realistic
                drawdown numbers.
              </p>
            </div>
          );
        })()}

        {/* Drawdown controls */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-arc-line">
          <label className="flex flex-col">
            <span className="eyebrow mb-2">Drawdown Period (years)</span>
            <input
              type="text"
              inputMode="numeric"
              value={drawdownYearsText}
              onChange={(e) => {
                const txt = e.target.value;
                setDrawdownYearsText(txt);
                if (txt === "") return;
                const n = parseInt(txt);
                if (!isNaN(n) && n > 0) setDrawdownYears(n);
              }}
              onBlur={() => {
                const n = parseInt(drawdownYearsText);
                if (isNaN(n) || n < 1) {
                  setDrawdownYearsText("30");
                  setDrawdownYears(30);
                } else {
                  setDrawdownYearsText(String(n));
                }
              }}
              className="px-3 py-2 rounded-lg text-sm num"
            />
          </label>
          <label className="flex flex-col">
            <span className="eyebrow mb-2">Expected Return During Drawdown (%)</span>
            <input
              type="text"
              inputMode="decimal"
              value={drawdownReturnText}
              onChange={(e) => {
                const txt = e.target.value;
                setDrawdownReturnText(txt);
                // Allow partial input like "" or "."
                if (txt === "" || txt === "." || txt === "-") return;
                const n = parseFloat(txt);
                if (!isNaN(n)) setDrawdownReturn(Math.max(0, n / 100));
              }}
              onBlur={() => {
                const n = parseFloat(drawdownReturnText);
                if (isNaN(n) || n < 0) {
                  setDrawdownReturnText("6.0");
                  setDrawdownReturn(0.06);
                } else {
                  setDrawdownReturnText(n.toFixed(1));
                }
              }}
              className="px-3 py-2 rounded-lg text-sm num"
            />
            {/* Reset-to-default helper, especially useful if user has set
                an unrealistic high return rate */}
            {drawdownReturn !== 0.06 && (
              <button
                type="button"
                onClick={() => {
                  setDrawdownReturn(0.06);
                  setDrawdownReturnText("6.0");
                }}
                className="btn-tactile self-start mt-2 text-[10px] font-mono uppercase tracking-[0.14em] text-arc-dim hover:text-arc-gold transition-colors"
                title="Reset to 6% — standard Fidelity / KeyBank planner default"
              >
                ↺ Reset to 6%
              </button>
            )}
          </label>
        </div>

        <p className="mt-4 text-[10px] font-mono uppercase tracking-[0.14em] text-arc-dim leading-relaxed">
          Your withdrawal number above uses standard amortization math: PMT = P · r ÷ (1 − (1 + r)⁻ⁿ).
          The benchmark rules use Bengen's historical research and don't depend on your inputs — they're
          there as a reality check.
        </p>
      </div>
    </div>
  );
};

export default RetirementTab;
